/**
 * Produktkarten: Bildwechsel beim Überfahren, Rücksprung beim Verlassen.
 *
 * Ersetzt Rebels previewImage/resetImage, die an der Kartengalerie deklarativ
 * hingen. Die beiden arbeiten mit relativen Schritten — zeigen ruft next(),
 * wegnehmen ruft previous(). Das geht auf, solange niemand die Pfeile benutzt;
 * danach zählt jeder Schritt falsch. previewImage stellt einen zuvor gewählten
 * Index ausserdem nur wieder her, wenn er grösser als 0 ist:
 *
 *     if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
 *       slideshow.select(this.#previousSlideIndex, ...);
 *     } else {
 *       slideshow.next(...);
 *     }
 *
 * Das erste Bild war damit über die Pfeile nicht erreichbar: nach rechts ging
 * es dort nicht weiter, und beim Durchblättern nach links übersprang der
 * Rundlauf es und landete direkt beim zweiten.
 *
 * Hier stehen stattdessen feste Zielfolien: 1 beim Überfahren, 0 beim
 * Verlassen. Ein Zustand, der sich nicht verzählen kann.
 *
 * Gleiches Verhalten wie im Hero: wer die Karte verlässt, sieht wieder das
 * Ausgangsbild, und Pfeile wie Punkte verschwinden — das erledigt CSS über
 * :hover. Eine frühere Fassung liess die Karte auf dem gewählten Bild stehen;
 * das ist bewusst aufgegeben, damit das Raster nach dem Überfliegen nicht in
 * zehn verschiedenen Zuständen dasteht.
 */

const GALERIE = '[data-safago-hover-swap]';

/** Nur Maus. Auf Touch gibt es kein Überfahren, dort wird gewischt. */
function istMaus(event) {
  return event.pointerType === 'mouse';
}

/**
 * Die Slideshow der Karte. Sie liegt innerhalb der Galerie, nicht daneben.
 * @param {Element} galerie
 * @returns {(Element & { select?: Function }) | null}
 */
function slideshowVon(galerie) {
  return galerie.querySelector('slideshow-component');
}

/**
 * @param {Element} galerie
 * @param {number} index
 */
function zeige(galerie, index) {
  const slideshow = slideshowVon(galerie);
  if (typeof slideshow?.select !== 'function') return;
  // Ohne Animation, wie im Theme: beim Überfahren soll das Bild wechseln,
  // nicht durchs Bild fahren.
  slideshow.select(index, undefined, { animate: false });
}

/**
 * Betritt der Zeiger die Galerie von aussen?
 *
 * pointerover feuert auch bei jedem Wechsel zwischen Kindelementen. Ohne die
 * Prüfung auf relatedTarget würde die Zielfolie bei jeder Mausbewegung neu
 * gesetzt — und ein über die Pfeile gewähltes Bild spränge sofort zurück.
 *
 * @param {PointerEvent} event
 */
function onPointerOver(event) {
  if (!istMaus(event)) return;
  const galerie = event.target instanceof Element ? event.target.closest(GALERIE) : null;
  if (!galerie) return;
  if (event.relatedTarget instanceof Node && galerie.contains(event.relatedTarget)) return;

  entbindePfeile(galerie);
  zeige(galerie, 1);
}

/** @param {PointerEvent} event */
function onPointerOut(event) {
  if (!istMaus(event)) return;
  const galerie = event.target instanceof Element ? event.target.closest(GALERIE) : null;
  if (!galerie) return;
  if (event.relatedTarget instanceof Node && galerie.contains(event.relatedTarget)) return;

  gibFokusFrei(galerie);
  zeige(galerie, 0);
}

/**
 * Fokus abräumen, sonst bleiben Pfeile und Punkte stehen.
 *
 * Ein Mausklick auf einen Pfeil lässt den Knopf fokussiert zurück. Sichtbar
 * gemacht werden beide Bedienelemente aber über :hover und :focus-within — die
 * zweite Bedingung griff dadurch weiter, obwohl der Zeiger die Karte längst
 * verlassen hatte. Sie verschwanden erst bei einem Klick irgendwo anders.
 *
 * Tastaturfokus bleibt unangetastet: :focus-visible unterscheidet ihn vom
 * Klickfokus, und wer sich mit der Tastatur durch das Raster arbeitet, darf
 * seine Stelle nicht verlieren, nur weil die Maus zufällig darüberstreift.
 *
 * @param {Element} galerie
 */
function gibFokusFrei(galerie) {
  const fokussiert = document.activeElement;
  if (!(fokussiert instanceof HTMLElement)) return;
  if (!galerie.contains(fokussiert)) return;
  if (fokussiert.matches(':focus-visible')) return;
  fokussiert.blur();
}

/**
 * Blättern vollständig selbst übernehmen.
 *
 * Rebels eigener Weg hat einen Fehler direkt nach dem Umlaufen: dabei ordnet
 * die Slideshow ihre Folien um, `current` meldet kurz noch den alten Wert, das
 * Theme rechnet daraus dasselbe Ziel, auf dem es ohnehin steht, und select()
 * steigt wegen
 *
 *     if (currentSlide === requestedSlideElement) return;
 *
 * sofort wieder aus. Sichtbar genau wie gemeldet: auf dem ersten Bild passiert
 * beim Klick nach rechts nichts.
 *
 * Zwei Auswege wurden verworfen. Den Theme-Handler per stopPropagation
 * abschneiden geht nicht — er hängt ebenfalls in der Capture-Phase an
 * document, und zwischen Zuhörern am selben Knoten greift das nicht; die Karte
 * sprang dann zwei Bilder weiter. Und nachträglich prüfen, ob der Klick
 * gewirkt hat, scheitert am fehlenden Messwert: am Rechner blättert die Karte
 * nicht durch Scrollen, scrollLeft bleibt 0.
 *
 * Also derselbe Griff wie oben beim Hover-Wechsel: dem Knopf das Attribut
 * `on:click` nehmen. Horizon liest es erst, wenn das Ereignis eintritt — ohne
 * Attribut tut der Theme-Handler nichts, und das Ziel wird hier bestimmt.
 * Abgeräumt wird beim ersten Überfahren, denn geklickt werden kann erst
 * danach; das überlebt auch das Neuaufbauen des Rasters durch die Filter.
 *
 * @param {Element} galerie
 */
function entbindePfeile(galerie) {
  for (const knopf of galerie.querySelectorAll('.slideshow-control[on\\:click]')) {
    knopf.removeAttribute('on:click');
  }
}

/** @param {MouseEvent} event */
function onArrowClick(event) {
  const knopf = event.target instanceof Element ? event.target.closest('.slideshow-control') : null;
  if (!knopf) return;

  const galerie = knopf.closest(GALERIE);
  const slideshow = galerie && slideshowVon(galerie);
  if (!slideshow || typeof slideshow.select !== 'function') return;

  const letzte = (slideshow.slides?.length ?? 0) - 1;
  if (letzte < 1) return;

  // Der Knopf liegt im Produktlink — ohne das hier landet der Klick dort.
  event.preventDefault();
  event.stopPropagation();

  const richtung = knopf.getAttribute('ref') === 'previous' ? -1 : 1;
  let ziel = slideshow.current + richtung;
  if (ziel < 0) ziel = letzte;
  if (ziel > letzte) ziel = 0;
  slideshow.select(ziel, undefined, { animate: false });
}

document.addEventListener('pointerover', onPointerOver);
document.addEventListener('pointerout', onPointerOut);
document.addEventListener('click', onArrowClick, true);
